import { useState } from "react";
import jsPDF from "jspdf";
import FederalProfileCard from "../FederalProfileCard";
import FederalOpsCard from "../FederalOpsCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COMPANY_INFO = {
  name: "Moovin Forward",
  owner: "Shannon Ware",
  title: "Chief Executive Officer",
  coOwner: "Shaneka Price",
  coTitle: "Chief Operations Officer",
  address: "DFW Metroplex, Texas",
  phone: "(817) 381-8831",
  email: "nivoomforward@gmail.com",
  website: "moovinforward.org",
  ein: "[EIN — insert yours]",
  duns: "[DUNS # — register free at dnb.com]",
  sam: "[SAM.gov UEI — register free at sam.gov]",
  naics_primary: "236118 — Residential Remodelers",
  naics_secondary: [
    "238320 — Painting and Wall Covering Contractors",
    "238330 — Flooring Contractors",
    "238390 — Other Building Finishing Contractors",
    "561720 — Janitorial Services",
    "561790 — Other Services to Buildings and Dwellings"
  ],
  sic: "1522 — General Contractors, Residential Buildings",
  state_of_formation: "Texas",
  entity_type: "LLC",
  year_established: "2024",
  annual_revenue: "[Insert current year revenue]",
  employees: "[Insert current headcount]",
  ownership_percent_women: "100%",
  ownership_percent_minority: "100%",
  ethnicity: "Black/African American",
  gender: "Female",
};

const CERTIFICATIONS = [
  {
    id: "wbe",
    name: "WBE — Women's Business Enterprise",
    agency: "WBENC (Women's Business Enterprise National Council)",
    local: "WBCS (Women's Business Council — Southwest, Dallas chapter)",
    website: "https://www.wbenc.org",
    localWebsite: "https://www.wbcsouthwest.org",
    cost: "$300-$500 (based on revenue)",
    timeline: "60-90 days",
    benefits: "Access to corporate supplier diversity programs at Fortune 500 companies. WBENC-certified businesses receive priority consideration from companies like Target, Walmart, Amazon, and major property management firms.",
    narrative: `COMPANY OVERVIEW
Moovin Forward, LLC is a 100% woman-owned property stabilization firm based in the Dallas-Fort Worth metroplex, Texas. The company was founded and is solely controlled by Shannon Ware (CEO, 100% ownership interest). Shaneka Price serves as Chief Operations Officer.

SERVICES
Moovin Forward performs interior unit turnovers for multifamily housing, public housing authorities, and single-family rental portfolios. Scope of work includes: full interior repaint (2-coat system, Sherwin Williams ProMar 200), luxury vinyl plank flooring installation, fixture and hardware replacement, drywall repair, deep cleaning (22-item checklist), and trash-out/debris removal. All finishes meet HUD NSPIRE and HQS inspection standards.

DIFFERENTIATORS
1. Activation Capital Model: Materials are funded and staged on-site prior to labor start. This eliminates the procurement delay that extends industry-standard turn times from 45 days to our documented 14-18 day average.
2. Standardized Palette System: Four pre-approved interior configurations ensure consistent, market-ready finishes across every unit without client-side material selection delays.
3. Documentation Standard: Every completed unit produces a Line-Itemized Documentation Packet containing timestamped before/during/after photography, GPS-verified crew check-in/out records, material receipts, and NSPIRE-ready compliance checklists. Packets are retained for 7 years.
4. Staggered Wave Deployment: Simultaneous processing of 20+ units using rolling crew assignments prevents budget spikes and eliminates idle time.

OWNER QUALIFICATIONS
Shannon Ware brings 20 years of progressive professional experience:
- Armed Security Officer through Assistant Account Manager (2004-2024) at DFW apartment communities
- CNA License #NA0061071340 (Level 3) — healthcare-grade compliance and sanitation documentation
- Direct operational experience in multifamily property access control, resident safety, and site management

MARKET POSITION
In the DFW property maintenance sector, women own fewer than 3% of firms. Black women own fewer than 1%. Moovin Forward operates in this underrepresented space while delivering measurably superior outcomes: 60% vacancy time compression, zero change orders, and audit-ready documentation on every unit.

CURRENT CLIENTS & TARGETS
Active pursuit of contracts with Dallas Housing Authority (DHA), Fort Worth Housing Solutions (FWHS), and institutional property management firms operating 500+ unit portfolios in Tarrant and Dallas counties.`,
    documents: [
      "Articles of Organization / Certificate of Formation (Texas SOS)",
      "Operating Agreement showing ownership percentages",
      "EIN Letter from IRS (CP 575 or SS-4 confirmation)",
      "Business bank statements (last 12 months)",
      "Personal tax returns (last 2 years) for Shannon Ware",
      "Business tax returns (last 2 years, or since inception)",
      "Current business license / permits",
      "Proof of US citizenship or legal residency (driver's license + passport or birth certificate)",
      "Resume / CV for Shannon Ware",
      "CNA License #NA0061071340",
      "Lease agreement or proof of business address",
      "Client reference letters (3 recommended)",
      "Photos of completed work / project documentation",
    ],
    steps: [
      "Go to wbcsouthwest.org (our local WBENC chapter in Dallas)",
      "Click 'Get Certified' or 'Apply Now'",
      "Create an account with your email (nivoomforward@gmail.com)",
      "Fill in the online form — when it asks for company info, come back here and hit 'Copy' on the Company Information section, then paste the answers into their form",
      "When it asks for a company description or narrative, hit 'Copy' on the Narrative section above and paste it in",
      "Upload the documents from the checklist below (they'll tell you which ones are required vs optional)",
      "Pay the application fee ($300-$500 based on revenue)",
      "Submit and wait 60-90 days — they may schedule a site visit or phone interview",
    ]
  },
  {
    id: "mbe",
    name: "MBE — Minority Business Enterprise",
    agency: "NMSDC (National Minority Supplier Development Council)",
    local: "DFW MSDC (Dallas/Fort Worth Minority Supplier Development Council)",
    website: "https://nmsdc.org",
    localWebsite: "https://dfwmsdc.com",
    cost: "$250-$500 (based on revenue)",
    timeline: "45-90 days",
    benefits: "Access to corporate procurement programs specifically for minority-owned businesses. NMSDC connects you with Fortune 500 companies and government contractors who have supplier diversity mandates.",
    narrative: `COMPANY OVERVIEW
Moovin Forward, LLC is a 100% minority-owned (Black/African American), 100% woman-owned property stabilization company headquartered in the Dallas-Fort Worth metroplex. Shannon Ware is the sole owner (100% equity interest) and controls all management decisions including bidding, contract execution, hiring, purchasing, and daily operations.

SERVICES & CAPABILITIES
The company performs interior unit stabilization for multifamily housing (100-500+ unit properties), public housing (HUD-assisted, Section 8, RAD conversion), and scattered-site single-family rentals. Core services: interior repaint (ProMar 200, 2-coat system), LVP flooring ($1.69/sq ft wholesale), fixture/hardware replacement, drywall repair, deep cleaning, and complete trash-out.

All work is executed to HUD NSPIRE and Housing Quality Standards (HQS) compliance levels. Documentation packets include line-itemized material manifests with receipts, timestamped GPS-verified crew logs, and before/during/after photo records.

COMPETITIVE ADVANTAGE
Moovin Forward's Activation Capital Model eliminates the payment-delay-work-delay cycle endemic to the property maintenance industry. Materials are procured at wholesale through established pro accounts (Sherwin Williams, Floor & Decor, Home Depot Pro) and staged on-site before labor begins. Result: 14-18 day average unit turn versus the 45-day industry standard — a 60% reduction in vacancy loss.

The company deploys crews using a Staggered Wave methodology that maintains constant throughput across 20+ simultaneous units without budget overruns.

OWNER BACKGROUND
Shannon Ware — 20 years professional experience:
- Armed Security Officer → Site Manager → Supervisor → Assistant Account Manager at DFW apartment communities (2004-2024)
- CNA License #NA0061071340 (Level 3) — healthcare compliance documentation standards
- Direct experience in multifamily property operations, resident safety protocols, and vendor management

MINORITY REPRESENTATION
In the DFW property maintenance sector, minority-owned firms represent fewer than 5% of vendors despite the metroplex's 42% minority population. Black-owned firms represent fewer than 2%. Moovin Forward addresses this disparity while delivering documented, measurably superior performance metrics.`,
    documents: [
      "Articles of Organization / Certificate of Formation",
      "Operating Agreement showing 51%+ minority ownership",
      "EIN Letter from IRS",
      "Business bank statements (last 12 months)",
      "Personal and business tax returns (last 2 years)",
      "Proof of ethnicity (birth certificate, passport, or tribal documentation)",
      "Proof of US citizenship",
      "Driver's license for Shannon Ware",
      "Resume / CV for Shannon Ware",
      "Business licenses and permits",
      "Client contracts or purchase orders (proof of operations)",
      "Signed affidavit of minority ownership",
    ],
    steps: [
      "Go to dfwmsdc.com (our local NMSDC chapter)",
      "Click 'Certification' or 'Apply'",
      "Create an account with your email",
      "Fill in the online form — when it asks for company info, come back here and hit 'Copy' on the Company Information section, then paste",
      "When it asks for a company description, hit 'Copy' on the Narrative section and paste it",
      "Upload the documents from the checklist below",
      "Pay the application fee ($250-$500 based on revenue)",
      "Submit and wait 45-90 days — they will schedule a site visit",
    ]
  },
  {
    id: "dbe",
    name: "DBE — Disadvantaged Business Enterprise",
    agency: "TxDOT (Texas Department of Transportation) / Federal DOT",
    local: "North Central Texas Council of Governments (NCTCOG)",
    website: "https://www.txdot.gov/business/disadvantaged-business-enterprise.html",
    localWebsite: "https://www.nctcog.org",
    cost: "Free",
    timeline: "60-120 days",
    benefits: "Required for federally-funded contracts (airports, transit, highways). DBE certification opens access to set-aside contracts from DART, DFW Airport, TxDOT, and municipal projects with federal funding.",
    narrative: `APPLICANT IDENTIFICATION
Moovin Forward, LLC — a socially and economically disadvantaged small business as defined under 49 CFR Part 26. The firm is 100% owned and controlled by Shannon Ware, a Black woman who is a United States citizen.

SOCIAL DISADVANTAGE
Shannon Ware is presumed socially disadvantaged as a Black American woman under 49 CFR 26.67(a). She has experienced chronic barriers to capital access, bonding, and contract opportunities in the property maintenance industry — a sector where Black women own fewer than 1% of firms nationally. Despite 20 years of professional experience in property operations and security management, access to institutional lending and surety bonding has been systematically limited compared to non-disadvantaged competitors.

ECONOMIC DISADVANTAGE
Shannon Ware's personal net worth (excluding primary residence and ownership interest in the applicant firm) falls below the $1.32 million threshold established under 49 CFR 26.67(b)(1). The firm's gross receipts fall below the SBA size standard for NAICS 236118 ($45 million). Detailed personal financial statements (SBA Form 413) will be provided with the application.

BUSINESS DESCRIPTION
Moovin Forward performs interior unit stabilization services for residential rental properties in the Dallas-Fort Worth metropolitan area. Services include: interior painting, flooring installation, fixture replacement, drywall repair, deep cleaning, and debris removal. All work meets HUD NSPIRE inspection standards.

MANAGEMENT AND CONTROL
Shannon Ware makes all management decisions for Moovin Forward including: contract bidding and negotiation, crew hiring and scheduling, material procurement, quality control, client relations, and financial management. No other individual or entity exercises control over the firm's operations, management, or decision-making.

CAPACITY
The firm operates a Staggered Wave Deployment system capable of processing 20+ simultaneous unit turns. Materials are pre-funded through wholesale pro accounts. Documentation meets court-grade and HUD audit requirements. GPS-verified crew tracking and timestamped photography are standard on every project.`,
    documents: [
      "SBA DBE Application Form (Uniform Certification Application)",
      "Personal Net Worth Statement (SBA Form 413)",
      "Articles of Organization / Certificate of Formation",
      "Operating Agreement",
      "Personal tax returns (last 3 years)",
      "Business tax returns (last 3 years, or since inception)",
      "Business bank statements (last 12 months)",
      "Personal bank statements (last 12 months)",
      "Proof of ethnicity and gender",
      "Resume / CV for Shannon Ware",
      "List of equipment owned or leased",
      "List of all contracts/projects in the last 12 months",
      "Proof of US citizenship",
      "EIN Letter",
    ],
    steps: [
      "Go to txdot.gov/business/disadvantaged-business-enterprise.html",
      "Click 'Apply for DBE Certification' — this takes you to the Uniform Certification Application",
      "Download and fill out the application — when it asks for company info, come back here and hit 'Copy' on the Company Information section",
      "When it asks you to describe your business, hit 'Copy' on the Narrative section and paste it in",
      "Fill out the Personal Net Worth Statement (SBA Form 413) — this is a one-page form listing your assets and debts",
      "Gather all documents from the checklist below — scan them as PDFs",
      "Submit everything by mail or online (depends on your state UCP — Texas uses TxDOT)",
      "This is FREE — no application fee",
      "Wait 60-120 days — they may request additional documents or schedule an interview",
    ]
  },
  {
    id: "sba8a",
    name: "SBA 8(a) — Business Development Program",
    agency: "U.S. Small Business Administration",
    local: "SBA Dallas/Fort Worth District Office",
    website: "https://www.sba.gov/federal-contracting/contracting-assistance-programs/8a-business-development-program",
    localWebsite: "https://www.sba.gov/offices/district/tx/dallas-fort-worth",
    cost: "Free",
    timeline: "90-180 days",
    benefits: "9-year federal program with sole-source contract authority up to $4.5M. Access to federal set-aside contracts, mentoring, management/technical assistance, and bonding assistance. This is the most powerful certification for government contracting.",
    narrative: `APPLICANT OVERVIEW
Moovin Forward, LLC applies for admission to the SBA 8(a) Business Development Program under 13 CFR 124. The firm is 100% owned and controlled by Shannon Ware, a Black American woman and United States citizen.

SOCIAL DISADVANTAGE NARRATIVE
Shannon Ware has faced chronic, substantial barriers in the property services industry attributable to her race and gender:

1. Capital Access: Traditional lenders have repeatedly declined business credit applications despite 20 years of professional experience and documented operational capacity. The firm operates on an Activation Capital model (client-funded materials) specifically because conventional working capital lines have been unavailable on terms comparable to those offered to non-disadvantaged competitors.

2. Industry Exclusion: In the DFW property maintenance sector, Black women own fewer than 1% of firms. Shannon Ware has been excluded from informal referral networks, vendor lists, and bid opportunities that are routinely accessible to established (non-disadvantaged) contractors. Breaking into institutional property management required building a technology platform (client portal, GPS crew tracking, automated documentation) that larger competitors are not required to demonstrate.

3. Bonding Barriers: Surety bonding — required for many public contracts — has been inaccessible at the firm's current revenue level, creating a circular barrier: without bonding, the firm cannot win the contracts that would generate the revenue to qualify for bonding.

ECONOMIC DISADVANTAGE NARRATIVE
Shannon Ware's personal net worth falls below the $750,000 SBA threshold (13 CFR 124.104). Her total assets including the primary residence fall below $6 million. Her average adjusted gross income over the preceding three years falls below $400,000. Detailed financial documentation will be submitted through certify.sba.gov.

BUSINESS DESCRIPTION & POTENTIAL FOR SUCCESS
Moovin Forward performs interior unit stabilization for multifamily housing, public housing authorities, and single-family rental portfolios. The firm has developed proprietary operational systems that demonstrate clear potential for growth:

- Activation Capital Model: Eliminates procurement delays by pre-funding materials through wholesale pro accounts
- Staggered Wave Deployment: Processes 20+ units simultaneously using rolling crew assignments
- Standardized Palette System: Four pre-approved interior configurations ensure consistent quality without selection delays
- Documentation Standard: Court-grade, NSPIRE-compliant packets with 7-year retention

These systems compress vacancy turn times from the 45-day industry average to 14-18 days — a 60% improvement that is directly measurable and independently verifiable.

PROGRAM OBJECTIVES
The 8(a) program would provide Moovin Forward access to:
- Sole-source federal contracts up to $4.5 million
- Federal set-aside contracts in property maintenance (NAICS 236118, 238320, 561720)
- Mentoring relationships with established federal contractors
- SBA-facilitated bonding assistance to overcome the current surety barrier
- Management and technical development resources

TARGET FEDERAL OPPORTUNITIES
Military housing (Corvias, Lendlease managed properties), VA medical facility maintenance, HUD-assisted housing rehabilitation, GSA facility maintenance contracts in the DFW region.`,
    documents: [
      "SBA 8(a) Application (filed through certify.sba.gov)",
      "Personal Financial Statement (SBA Form 413)",
      "Personal tax returns (last 3 years)",
      "Business tax returns (last 3 years, or since inception)",
      "Business financial statements (balance sheet, P&L)",
      "Articles of Organization / Certificate of Formation",
      "Operating Agreement",
      "Business bank statements (last 12 months)",
      "Personal bank statements (last 12 months)",
      "Resume / CV showing industry experience",
      "Business plan or capability statement",
      "Proof of US citizenship (born in US or naturalized)",
      "DUNS Number / SAM.gov UEI registration",
      "Proof of ethnicity (birth certificate recommended)",
      "One-page narrative of social disadvantage",
      "One-page narrative of economic disadvantage",
    ],
    steps: [
      "FIRST: Register at SAM.gov (sam.gov) if you haven't — this is required before you can apply. It's free but takes 7-10 days to process",
      "FIRST: Get your DUNS number at dnb.com — also free, takes 1-2 days",
      "Go to certify.sba.gov and create an account",
      "Click '8(a) Business Development' and start a new application",
      "The application is long (plan 2-3 hours) — it asks about your company, your personal finances, and your story",
      "When it asks for company description, hit 'Copy' on the Narrative section above and paste it in — the social and economic disadvantage narratives are already written for you in there",
      "Fill out the Personal Financial Statement (SBA Form 413) — lists your assets, debts, income",
      "Upload all documents from the checklist below as PDFs",
      "This is FREE — no application fee",
      "Submit and wait 90-180 days — SBA may request clarification or additional documents",
      "TIP: Call the SBA Dallas/Fort Worth office at (817) 684-5500 after you submit to confirm they received it",
    ]
  },
  {
    id: "hubzone",
    name: "HUBZone — Historically Underutilized Business Zone",
    agency: "U.S. Small Business Administration",
    local: "SBA Dallas/Fort Worth District Office",
    website: "https://www.sba.gov/federal-contracting/contracting-assistance-programs/hubzone-program",
    localWebsite: "https://maps.certify.sba.gov/hubzone/map",
    cost: "Free",
    timeline: "60-90 days",
    benefits: "10% price evaluation preference on federal contracts. Access to HUBZone set-aside contracts. Can be combined with 8(a) for maximum federal contracting power.",
    narrative: `APPLICANT OVERVIEW
Moovin Forward, LLC applies for HUBZone certification under 13 CFR 126. The firm's principal office is located in the Dallas-Fort Worth metroplex within a designated HUBZone-qualifying census tract.

PRINCIPAL OFFICE
Moovin Forward's principal office — where the greatest number of employees report to work and where management directs daily operations — is located in [INSERT FULL ADDRESS], which falls within a qualified HUBZone area as verified through the SBA HUBZone Map tool (maps.certify.sba.gov/hubzone/map).

EMPLOYEE RESIDENCY REQUIREMENT
Moovin Forward certifies that at least 35% of its employees reside in HUBZone-designated areas. The company's workforce is drawn primarily from communities within Tarrant and Dallas counties that qualify as HUBZones, including census tracts in Fort Worth, Arlington, south Dallas, and surrounding municipalities.

Employee residency documentation (driver's licenses, utility bills, lease agreements) demonstrating HUBZone residence will be provided with the application.

BUSINESS DESCRIPTION
The company performs interior unit stabilization services for residential rental properties: painting, flooring, fixtures, hardware, drywall repair, deep cleaning, and debris removal. All work meets HUD NSPIRE inspection standards. The firm processes 20+ simultaneous unit turns using a Staggered Wave Deployment system with GPS-verified crew tracking and court-grade documentation.

COMMUNITY IMPACT
Every unit Moovin Forward stabilizes creates paid work for residents of historically underutilized communities while directly improving housing quality in their neighborhoods. The company's hiring practices prioritize residents of the same communities where projects are located, reinforcing the HUBZone program's mission of economic development through local job creation.

OWNER
Shannon Ware (100% owner) — Black American woman, US citizen, 20 years professional experience in property operations and security management. CNA License #NA0061071340 (Level 3).`,
    documents: [
      "HUBZone application (filed through certify.sba.gov)",
      "Proof of principal office location (lease, utility bill)",
      "Employee roster with home addresses",
      "Proof of employee residency in HUBZone (utility bills, driver's licenses)",
      "Map verification via SBA HUBZone map tool",
      "Articles of Organization / Certificate of Formation",
      "Operating Agreement",
      "EIN Letter",
      "SAM.gov UEI registration",
    ],
    steps: [
      "FIRST: Check if your address is in a HUBZone — go to maps.certify.sba.gov/hubzone/map and type in your business address. If it says 'Qualified HUBZone Area' you're eligible",
      "FIRST: You need SAM.gov registration (same as 8(a) — if you already registered for 8(a), you're good)",
      "Go to certify.sba.gov and log in (same account as 8(a))",
      "Click 'HUBZone' and start a new application",
      "It will ask for your business address — enter your principal office address",
      "It will ask for employee addresses — list each employee/crew member with their home address",
      "At least 35% of your employees must live in a HUBZone — check each address on the HUBZone map",
      "Upload proof: your lease or utility bill for the office, and driver's licenses or utility bills for employees showing HUBZone addresses",
      "This is FREE — no application fee",
      "Submit and wait 60-90 days",
      "TIP: You can apply for HUBZone and 8(a) at the same time — having both gives you maximum federal contracting power",
    ]
  },
];

const CertificationsTab = () => {
  const [activeCert, setActiveCert] = useState("wbe");
  const [copied, setCopied] = useState(false);
  const [copiedSection, setCopiedSection] = useState(null);
  const cert = CERTIFICATIONS.find(c => c.id === activeCert);

  const copyText = (text, section) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 2000);
    });
  };

  const copyAll = () => {
    if (!cert) return;
    const companyBlock = Object.entries(COMPANY_INFO).map(([k, v]) => {
      if (Array.isArray(v)) return `${k}: ${v.join(", ")}`;
      return `${k}: ${v}`;
    }).join("\n");
    const full = `=== ${cert.name} APPLICATION PACKAGE ===\n\nAgency: ${cert.agency}\nLocal: ${cert.local}\nWebsite: ${cert.website}\nCost: ${cert.cost}\nTimeline: ${cert.timeline}\n\n--- COMPANY INFORMATION ---\n${companyBlock}\n\n--- NARRATIVE / COMPANY DESCRIPTION ---\n${cert.narrative}\n\n--- REQUIRED DOCUMENTS CHECKLIST ---\n${cert.documents.map((d, i) => `${i + 1}. [ ] ${d}`).join("\n")}\n\n--- BENEFITS ---\n${cert.benefits}`;
    navigator.clipboard.writeText(full).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const downloadLeadPaintSOP = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const M = 50;
    let y = M;
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const lh = 14;

    const writeWrapped = (text, opts = {}) => {
      const size = opts.size || 10;
      const bold = opts.bold || false;
      const color = opts.color || [40, 40, 40];
      doc.setFontSize(size);
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setTextColor(...color);
      const lines = doc.splitTextToSize(text, pageW - M * 2);
      lines.forEach(line => {
        if (y > pageH - M) { doc.addPage(); y = M; }
        doc.text(line, M, y);
        y += lh;
      });
    };

    const heading = (text) => {
      y += 6;
      writeWrapped(text, { size: 12, bold: true, color: [0, 130, 130] });
      y += 2;
    };

    // Title
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(20, 20, 20);
    doc.text("Lead-Safe Renovation SOP", M, y); y += 24;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(110, 110, 110);
    doc.text("Moovin Forward LLC - EPA RRP Compliance Standard Operating Procedure", M, y); y += 12;
    doc.text(`Generated: ${new Date().toLocaleDateString()} | Reference: BACKSPLASH_SOP.md`, M, y); y += 22;

    heading("1. WHEN THIS SOP APPLIES");
    writeWrapped("This procedure is mandatory on any unit built before 1978, regardless of whether visible lead paint chips, peeling, or cracking is observed. EPA's Renovation, Repair, and Painting (RRP) Rule classifies pre-1978 housing as presumed lead-containing until tested otherwise.");

    heading("2. CERTIFICATION REQUIRED");
    writeWrapped("- Firm: Moovin Forward LLC must hold an EPA Lead-Safe Certified Firm registration (renewed every 5 years).");
    writeWrapped("- Renovator: At least one Certified Renovator must be on-site during all paint-disturbing work.");
    writeWrapped("- Penalty for non-compliance: Up to $37,500 per violation per day.");

    heading("3. PRE-WORK CHECKLIST (BEFORE ENTERING UNIT)");
    [
      "Verify property year built. If pre-1978 -> Lead-Safe protocol required.",
      "Confirm Certified Renovator is on-site OR will be present for the entire paint-disturbing phase.",
      "Distribute the EPA Renovate Right pamphlet to the property owner / occupant. Obtain signed acknowledgment.",
      "Photograph all surfaces to be disturbed. Tag photos with date/time/unit address.",
      "Stage containment supplies: 6-mil plastic sheeting, painter tape, HEPA vacuum, disposable gloves/coveralls, N100 respirators, wet-wipes, contractor-grade trash bags."
    ].forEach(item => writeWrapped(`[ ] ${item}`));

    heading("4. CONTAINMENT SETUP");
    [
      "Interior work: Cover the floor with 6-mil plastic extending at least 6 ft from the work area. Tape edges fully sealed.",
      "Close and seal all HVAC vents in the work area with plastic + tape.",
      "Close and seal all doors leading to non-work areas. Hang plastic sheeting at doorway openings.",
      "Exterior work: Plastic sheeting extending at least 10 ft from the structure on the ground.",
      "No occupants (including pets) inside the work zone during paint-disturbing activity."
    ].forEach(item => writeWrapped(`[ ] ${item}`));

    heading("5. WORK PRACTICES (PROHIBITED METHODS)");
    writeWrapped("The following methods are STRICTLY PROHIBITED on pre-1978 surfaces:", { bold: true });
    [
      "Open-flame burning or torching of paint",
      "Heat guns operating above 1,100 degrees F",
      "Power-sanding, grinding, planing, or blasting WITHOUT HEPA exhaust attachment",
      "Dry scraping or dry sanding more than 6 sq ft (interior) or 20 sq ft (exterior) without containment"
    ].forEach(item => writeWrapped(`X ${item}`, { color: [180, 0, 0] }));

    heading("6. APPROVED WORK PRACTICES");
    [
      "Wet methods: mist surface with water before scraping or sanding.",
      "HEPA-attached power tools only.",
      "Score the perimeter of any work area before disturbing paint.",
      "Mini-enclosures for small areas (window sash, door frame).",
      "Modular / removable installations (per Moovin Forward Collection) reduce paint-disturbing scope wherever possible."
    ].forEach(item => writeWrapped(`- ${item}`));

    heading("7. CLEANUP PROTOCOL");
    [
      "HEPA-vacuum all surfaces in the work area (floors, sills, walls within 2 ft).",
      "Wet-wipe all horizontal surfaces with EPA-approved cleaner.",
      "HEPA-vacuum a second time after wet-wipe.",
      "Roll up plastic sheeting INWARD (contaminated side in). Bag in heavy-duty contractor bags. Double-bag and seal.",
      "Dispose of all waste as construction debris (NOT hazardous waste under RRP rules in most jurisdictions; verify with local landfill).",
      "Conduct a visual cleanup verification: NO dust, paint chips, or residue visible."
    ].forEach((item, i) => writeWrapped(`${i + 1}. ${item}`));

    heading("8. POST-WORK DOCUMENTATION (RECORD-RETENTION: 3 YEARS)");
    [
      "Property address, date(s) of work, scope of paint-disturbing activity",
      "Certified Renovator name + certification number",
      "Signed Renovate Right acknowledgment from owner/occupant",
      "Photos: before, during containment, after cleanup",
      "Receipt-level Relay debit-card audit trail for materials",
      "Cleanup verification card signed by Certified Renovator"
    ].forEach(item => writeWrapped(`[ ] ${item}`));

    heading("9. INSPECTION-COMPLIANCE CROSS-REFERENCE");
    writeWrapped("This SOP also satisfies the lead-safe documentation requirements for:");
    writeWrapped("- HUD NSPIRE inspections (pre-1978 properties)");
    writeWrapped("- Section 8 / HQS inspections (pre-1978 properties)");
    writeWrapped("- City Code Enforcement (DFW Metroplex municipalities)");
    writeWrapped("- HUD Lead Disclosure Rule (24 CFR Part 35)");

    heading("10. EMERGENCY CONTACT");
    writeWrapped("EPA RRP Hotline: 1-800-424-LEAD (1-800-424-5323)");
    writeWrapped("Moovin Forward Compliance Lead: hello@moovinforward.org | (817) 381-8831");

    // Footer
    if (y > pageH - M - 40) { doc.addPage(); y = M; } else { y += 10; }
    doc.setDrawColor(200, 200, 200);
    doc.line(M, y, pageW - M, y); y += 14;
    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(140, 140, 140);
    doc.text("Moovin Forward LLC | DFW ROI Stabilization Firm | This SOP supplements but does not replace EPA RRP training.", M, y);

    doc.save(`MF-Lead-Safe-SOP-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="dash-tab-content" data-testid="certifications-tab">
      <h2>Business Certifications</h2>
      <p className="ops-lead">Apply for government and corporate certifications to unlock set-aside contracts, supplier diversity programs, and federal opportunities. Each package includes pre-filled company info, a professional narrative, and a document checklist.</p>

      <FederalProfileCard />
      <FederalOpsCard />

      <div className="cert-sop-bar" data-testid="cert-sop-bar">
        <div>
          <strong>Lead-Safe Renovation SOP (EPA RRP)</strong>
          <p>Required field protocol for any pre-1978 housing repair work. Print one per truck.</p>
        </div>
        <button onClick={downloadLeadPaintSOP} className="cert-sop-btn" data-testid="download-lead-paint-sop">
          Download Lead Paint SOP (PDF)
        </button>
      </div>

      <div className="cert-tabs" data-testid="cert-tabs">
        {CERTIFICATIONS.map(c => (
          <button key={c.id} className={`cert-tab-btn ${activeCert === c.id ? "active" : ""}`} onClick={() => { setActiveCert(c.id); setCopied(false); setCopiedSection(null); }} data-testid={`cert-tab-${c.id}`}>
            {c.id.toUpperCase()}
          </button>
        ))}
      </div>

      {cert && (
        <div className="cert-content" data-testid={`cert-content-${cert.id}`}>
          <div className="cert-header">
            <h3>{cert.name}</h3>
            <div className="cert-meta">
              <span className="cert-meta-item">Agency: {cert.agency}</span>
              {cert.local && <span className="cert-meta-item">Local: {cert.local}</span>}
              <span className="cert-meta-item">Cost: {cert.cost}</span>
              <span className="cert-meta-item">Timeline: {cert.timeline}</span>
            </div>
            <div className="cert-links">
              <a href={cert.website} target="_blank" rel="noopener noreferrer" className="cert-link" data-testid={`cert-link-${cert.id}`}>Apply Here</a>
              {cert.localWebsite && <a href={cert.localWebsite} target="_blank" rel="noopener noreferrer" className="cert-link cert-link-local">Local Chapter</a>}
            </div>
          </div>

          <div className="cert-benefits">
            <h4>Why Get This Certification</h4>
            <p>{cert.benefits}</p>
          </div>

          {cert.steps && (
            <div className="cert-howto" data-testid="cert-howto">
              <h4>How To Apply (Step by Step)</h4>
              <div className="cert-steps">
                {cert.steps.map((step, i) => (
                  <div key={i} className={`cert-step ${step.startsWith("FIRST:") ? "cert-step-prereq" : ""} ${step.startsWith("TIP:") ? "cert-step-tip" : ""}`}>
                    <span className="cert-step-num">{step.startsWith("FIRST:") ? "!" : step.startsWith("TIP:") ? "*" : i + 1}</span>
                    <span className="cert-step-text">{step.replace(/^FIRST: /, "").replace(/^TIP: /, "")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="cert-section">
            <div className="cert-section-header">
              <h4>Company Information (Pre-Filled)</h4>
              <button onClick={() => copyText(Object.entries(COMPANY_INFO).map(([k, v]) => Array.isArray(v) ? `${k}: ${v.join(", ")}` : `${k}: ${v}`).join("\n"), "company")} className="cert-copy-btn" data-testid="cert-copy-company">{copiedSection === "company" ? "Copied!" : "Copy"}</button>
            </div>
            <div className="cert-info-grid">
              {Object.entries(COMPANY_INFO).map(([key, val]) => (
                <div key={key} className="cert-info-row">
                  <span className="cert-info-label">{key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>
                  <span className="cert-info-val">{Array.isArray(val) ? val.join(", ") : val}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="cert-section">
            <div className="cert-section-header">
              <h4>Narrative / Company Description</h4>
              <button onClick={() => copyText(cert.narrative, "narrative")} className="cert-copy-btn" data-testid="cert-copy-narrative">{copiedSection === "narrative" ? "Copied!" : "Copy"}</button>
            </div>
            <pre className="cert-narrative">{cert.narrative}</pre>
          </div>

          <div className="cert-section">
            <div className="cert-section-header">
              <h4>Required Documents Checklist</h4>
              <button onClick={() => copyText(cert.documents.map((d, i) => `${i + 1}. [ ] ${d}`).join("\n"), "docs")} className="cert-copy-btn" data-testid="cert-copy-docs">{copiedSection === "docs" ? "Copied!" : "Copy"}</button>
            </div>
            <div className="cert-docs-list">
              {cert.documents.map((doc, i) => (
                <label key={i} className="cert-doc-item" data-testid={`cert-doc-${i}`}>
                  <input type="checkbox" className="cert-doc-check" />
                  <span>{doc}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="cert-actions">
            <button onClick={copyAll} className="cert-copy-all-btn" data-testid="cert-copy-all">{copied ? "Entire Package Copied!" : "Copy Entire Application Package"}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificationsTab;
